/**
 * A share link's view history (1.6.3), for administrators: when the link page was opened, the proxy downloaded, or a
 * decision or comment made on it, with the IP address and the browser. Opened from Administration → Shared links and
 * from the History button on an asset's Sharing tab.
 */
import { Eye } from 'lucide-react';
import type { ShareViewKind } from '@prism/contracts';
import { useShareViews } from '../api/hooks';
import { browserLabel, dateTime, relative } from '../lib/format';
import { Modal } from './Modal';
import { TableScroll } from './TableScroll';
import { LoadMore } from './LoadMore';
import { EmptyState } from './EmptyState';

export const SHARE_VIEW_KIND_LABEL: Record<ShareViewKind, string> = {
  view: 'Viewed', download: 'Downloaded', decision: 'Approved or requested changes', comment: 'Commented',
};

/** "View history … is kept for 90 days. …", from the server's historyDays (0: kept with no limit). */
export function historyNote(days: number | undefined): string {
  const kept = days === 0 ? 'is kept with no time limit' : `is kept for ${days ?? 90} days`;
  return `View history (time, IP address, browser) ${kept}. The view count covers the link’s whole life.`;
}

/**
 * 1.6.4: a link older than the view history (1.6.3) counts views the history never listed. When its view count is
 * above the page views recorded, say so instead of looking like a bug: "5 earlier views (before the history started
 * on 27 Oct 2026) are counted but not listed". Views that aged out of the kept history are named that way. null: none.
 */
export function earlierViewsNote(views: number | undefined, recorded: number | undefined, since: string | null | undefined, days: number | undefined, now = Date.now()): string | null {
  if (views == null || recorded == null) return null;
  const n = views - recorded;
  if (n <= 0) return null;
  const counted = `${n.toLocaleString()} earlier view${n === 1 ? '' : 's'}`;
  const verb = n === 1 ? 'is' : 'are';
  const startedWithinKept = since && (!days || now - new Date(since).getTime() <= days * 86_400_000);
  if (since && startedWithinKept) {
    const date = new Date(since).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    return `${counted} (before the history started on ${date}) ${verb} counted but not listed.`;
  }
  return `${counted} (older than the ${days ?? 90} days kept) ${verb} counted but not listed.`;
}

export function ShareViewsModal({ share, onClose }: { share: { id: string; tokenHint: string; views?: number }; onClose: () => void }) {
  const q = useShareViews(share.id);
  const items = q.data?.items ?? [];
  const days = q.data?.historyDays;
  const earlier = q.data ? earlierViewsNote(share.views, q.data.recordedViews, q.data.historySince, days) : null;
  return (
    <Modal title={`Views of …${share.tokenHint}`} onClose={onClose} wide actions={<button className="btn" onClick={onClose}>Close</button>}>
      <div data-testid="share-views">
        <p className="faint" style={{ margin: '0 0 10px', fontSize: 12.5 }} data-testid="share-views-note">
          {historyNote(days)}{share.views != null ? ` Opened ${share.views.toLocaleString()} time${share.views === 1 ? '' : 's'} in all.` : ''}
        </p>
        {q.isLoading ? <div className="skeleton" style={{ height: 120 }} /> : q.isError ? <p style={{ color: 'var(--danger)' }}>The view history could not be loaded.</p> : items.length === 0 ? (
          <EmptyState style={{ minHeight: 140 }} icon={<Eye size={22} />} title={days === 0 ? 'No views recorded' : `No views in the last ${days ?? 90} days`} testId="share-views-empty">
            {earlier && <p className="muted" data-testid="share-views-earlier">{earlier}</p>}
          </EmptyState>
        ) : (
          <>
          {earlier && <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }} data-testid="share-views-earlier">{earlier}</p>}
          <TableScroll>
            <table className="table compact" aria-label="View history">
              <thead><tr><th>Time</th><th>What</th><th>IP address</th><th>Browser</th></tr></thead>
              <tbody>
                {items.map((v, i) => (
                  <tr key={`${v.at}-${i}`} data-testid="share-view" data-kind={v.kind} style={{ cursor: 'default' }}>
                    <td className="muted nowrap" title={relative(v.at)}>{dateTime(v.at)}</td>
                    <td className="nowrap">{SHARE_VIEW_KIND_LABEL[v.kind]}</td>
                    <td className="mono nowrap" data-testid="share-view-ip">{v.ip ?? '—'}</td>
                    <td className="muted wrap" title={v.userAgent ?? undefined}>{browserLabel(v.userAgent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          </>
        )}
        <LoadMore list={q} shown={items.length} noun="views" />
      </div>
    </Modal>
  );
}
